import React from 'react';
import { aboutContent } from '../../content/about-content';

/**
 * What Is Neuropedagogy Section
 *
 * Introduces the concept with key principles and the rail network analogy.
 */

// Icons for the rail network analogy
const RailIcons = {
  signal: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 20h.01" />
      <path d="M7 20v-4" />
      <path d="M12 20v-8" />
      <path d="M17 20V8" />
      <path d="M22 4v16" />
    </svg>
  ),
  cloud: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.5 19H9a7 7 0 1 1 6.71-9h1.79a4.5 4.5 0 1 1 0 9Z" />
    </svg>
  ),
  stations: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="4" y="2" width="16" height="20" rx="2" ry="2" />
      <path d="M9 22v-4h6v4" />
      <path d="M8 6h.01" />
      <path d="M16 6h.01" />
      <path d="M12 6h.01" />
      <path d="M12 10h.01" />
      <path d="M12 14h.01" />
      <path d="M16 10h.01" />
      <path d="M16 14h.01" />
      <path d="M8 10h.01" />
      <path d="M8 14h.01" />
    </svg>
  ),
  dispatch: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
      <line x1="8" y1="21" x2="16" y2="21" />
      <line x1="12" y1="17" x2="12" y2="21" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  )
};

const WhatIsNeuropedagogy = React.forwardRef(({ id = 'what-is' }, ref) => {
  const { whatIsNeuropedagogy } = aboutContent;

  return (
    <section id={id} ref={ref} className="about-section">
      <header className="about-section__header">
        <h2 className="about-section__title">
          <svg className="about-section__title-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <path d="M12 16v-4" />
            <path d="M12 8h.01" />
          </svg>
          {whatIsNeuropedagogy.title}
        </h2>
      </header>

      <div className="about-section__content">
        <p>{whatIsNeuropedagogy.definition}</p>

        {/* Distinction callout */}
        <div className="callout-box">
          <h3 className="callout-box__title">{whatIsNeuropedagogy.distinction.title}</h3>
          <p className="callout-box__content">{whatIsNeuropedagogy.distinction.content}</p>
        </div>

        {/* Key Principles */}
        <h3 style={{
          fontFamily: 'var(--lrbrain-font-mono)',
          fontSize: '0.9rem',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          color: 'var(--lrbrain-navy)',
          marginTop: '2rem',
          marginBottom: '1rem'
        }}>
          Key Principles
        </h3>

        <div className="info-cards">
          {whatIsNeuropedagogy.keyPrinciples.map((principle, index) => (
            <div key={index} className="info-card info-card--highlighted">
              <h4 className="info-card__title">{principle.term}</h4>
              <p className="info-card__content">{principle.explanation}</p>
            </div>
          ))}
        </div>

        {/* Rail Network Analogy */}
        <h3 style={{
          fontFamily: 'var(--lrbrain-font-mono)',
          fontSize: '0.9rem',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          color: 'var(--lrbrain-navy)',
          marginTop: '2.5rem',
          marginBottom: '0.5rem'
        }}>
          The Rail Network Analogy
        </h3>
        <p style={{ color: 'var(--lrbrain-text-light)', marginBottom: '1rem' }}>
          {whatIsNeuropedagogy.railNetworkAnalogy.intro}
        </p>

        <div className="rail-network">
          {whatIsNeuropedagogy.railNetworkAnalogy.elements.map((element) => (
            <div key={element.id} className="rail-network__item">
              <div className="rail-network__icon">
                {RailIcons[element.icon]}
              </div>
              <div className="rail-network__label">{element.label}</div>
              <div className="rail-network__desc">{element.description}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
});

WhatIsNeuropedagogy.displayName = 'WhatIsNeuropedagogy';

export default WhatIsNeuropedagogy;
